import React, { useEffect, useRef, useState } from "react";
import { Logo } from "./Logo";
import { CalendarHeart, ChevronDown, X, Menu } from "lucide-react";

const links = [
  { id: "sobre",       label: "Sobre mí"    },
  { id: "diplomados",  label: "Formación"   },
  { id: "enfoque",     label: "Mi enfoque"  },
  { id: "testimonios", label: "Testimonios" },
  { id: "faq",         label: "FAQ"         },
  { id: "contacto",    label: "Contacto"    },
];

const serviciosLinks = [
  { id: "especialidades", label: "Especialidades" },
  { id: "modalidades",    label: "Modalidad de atención" },
  { id: "precios",        label: "Precios" },
];

const allIds = [...links, ...serviciosLinks].map((l) => l.id);

const Nav = () => {
  const [active, setActive]         = useState("inicio");
  const [mobileOpen, setMobileOpen] = useState(false);
  const [dropOpen, setDropOpen]     = useState(false);
  const dropRef                     = useRef(null);

  useEffect(() => {
    const onScroll = () => {
      let current = "inicio";
      for (const id of allIds) {
        const el = document.getElementById(id);
        if (!el) continue;
        if (el.getBoundingClientRect().top <= 140) current = id;
      }
      setActive(current);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const handler = (e) => {
      if (dropRef.current && !dropRef.current.contains(e.target)) {
        setDropOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Bloquea scroll cuando el menú mobile está abierto
  useEffect(() => {
    document.body.style.overflow = mobileOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [mobileOpen]);

  const serviciosActive = serviciosLinks.some((l) => l.id === active);

  return (
    <header
      className="fixed top-0 left-0 right-0 z-50 bg-[var(--cream-50)] shadow-[0_2px_24px_-8px_rgba(74,58,47,0.18)]"
      data-testid="site-nav"
    >
      <div className="max-w-[1320px] mx-auto flex items-center justify-between px-4 lg:px-10 py-2.5 lg:py-4">

        {/* Logo — más pequeño en mobile */}
        <a href="#inicio" className="shrink-0">
          {/* Mobile: logo reducido */}
          <span className="lg:hidden">
            <svg width="26" height="34" viewBox="0 0 46 58" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M2.5 22 C2.5 10, 12 1.5, 23 1.5 C34 1.5, 43.5 10, 43.5 22 L43.5 56.5 L2.5 56.5 Z"
                stroke="#2B1F18" strokeWidth="1.4" fill="none" />
              <path d="M23 16 C23 24, 23 36, 23 44" stroke="#B8694F" strokeWidth="1.2" fill="none" strokeLinecap="round" />
              <path d="M23 22 C19 21, 16 23, 15 26 C18 27, 21 25, 23 22 Z" fill="#B8694F" opacity="0.85" />
              <path d="M23 28 C27 27, 30 29, 31 32 C28 33, 25 31, 23 28 Z" fill="#B8694F" opacity="0.85" />
              <path d="M23 34 C19 33, 16 35, 15 38 C18 39, 21 37, 23 34 Z" fill="#B8694F" opacity="0.85" />
            </svg>
          </span>
          {/* Desktop: logo completo */}
          <span className="hidden lg:block">
            <Logo />
          </span>
        </a>

        {/* Desktop nav links */}
        <nav className="hidden lg:flex items-center justify-center flex-1 flex-nowrap gap-x-5 xl:gap-x-7 px-2">
          {links.map((l) => (
            <a key={l.id} href={`#${l.id}`}
              className={`nav-link ${active === l.id ? "active" : ""}`}
              data-testid={`nav-link-${l.id}`}>
              {l.label}
            </a>
          ))}
          <div className="relative" ref={dropRef}>
            <button
              onClick={() => setDropOpen((v) => !v)}
              className={`nav-link flex items-center gap-1 bg-transparent border-none cursor-pointer ${serviciosActive ? "active" : ""}`}
              aria-haspopup="true" aria-expanded={dropOpen}
              data-testid="nav-servicios-toggle"
            >
              Servicios
              <ChevronDown size={13} strokeWidth={2}
                className="transition-transform duration-200"
                style={{ transform: dropOpen ? "rotate(180deg)" : "rotate(0deg)" }} />
            </button>
            {dropOpen && (
              <div className="absolute top-[calc(100%+14px)] left-1/2 -translate-x-1/2 w-52 bg-white rounded-2xl shadow-[0_12px_32px_-8px_rgba(59,42,26,0.18)] border border-[var(--cream-200)] py-2 z-50" role="menu">
                {serviciosLinks.map((l, i) => (
                  <a key={l.id} href={`#${l.id}`} role="menuitem"
                    onClick={() => setDropOpen(false)}
                    className={`flex items-center px-4 py-2.5 text-[13.5px] font-medium transition-colors
                      ${active === l.id ? "text-[var(--terra-500)] bg-[var(--cream-50)]" : "text-[var(--ink-700)] hover:text-[var(--terra-500)] hover:bg-[var(--cream-50)]"}
                      ${i < serviciosLinks.length - 1 ? "border-b border-[var(--cream-100)]" : ""}`}
                    data-testid={`nav-link-${l.id}`}>
                    {l.label}
                  </a>
                ))}
              </div>
            )}
          </div>
        </nav>

        {/* Desktop CTA */}
        <a href="#contacto" className="btn-pill btn-pill-primary hidden lg:inline-flex shrink-0"
          data-testid="nav-cta-agenda">
          <CalendarHeart size={16} strokeWidth={2} />
          Agenda tu sesión
        </a>

        {/* Hamburger — solo mobile */}
        <button
          aria-label={mobileOpen ? "Cerrar menú" : "Abrir menú"}
          onClick={() => setMobileOpen((v) => !v)}
          className="lg:hidden flex flex-col items-center justify-center w-9 h-9 rounded-full bg-[var(--peach-100)] border border-[var(--peach-200)] shrink-0"
          data-testid="mobile-menu-toggle"
        >
          {mobileOpen
            ? <X size={16} className="text-[var(--terra-500)]" strokeWidth={2} />
            : <Menu size={16} className="text-[var(--terra-500)]" strokeWidth={2} />
          }
        </button>
      </div>

      {/* Mobile menu drawer */}
      {mobileOpen && (
        <div className="lg:hidden bg-[var(--cream-50)] border-t border-[var(--cream-200)] px-5 py-4 flex flex-col gap-0.5 shadow-[0_8px_24px_-8px_rgba(59,42,26,0.18)]">
          {links.map((l) => (
            <a key={l.id} href={`#${l.id}`}
              onClick={() => setMobileOpen(false)}
              className={`py-3 border-b border-[var(--cream-100)] text-[14px] font-medium ${active === l.id ? "text-[var(--terra-500)]" : "text-[var(--ink-700)]"}`}
              data-testid={`mobile-nav-link-${l.id}`}>
              {l.label}
            </a>
          ))}
          <p className="pt-3 pb-1 text-[10px] font-semibold tracking-[0.18em] uppercase text-[var(--terra-500)]">
            Servicios
          </p>
          {serviciosLinks.map((l) => (
            <a key={l.id} href={`#${l.id}`}
              onClick={() => setMobileOpen(false)}
              className={`py-2.5 pl-3 border-b border-[var(--cream-100)] text-[13.5px] ${active === l.id ? "text-[var(--terra-500)]" : "text-[var(--ink-700)]"}`}
              data-testid={`mobile-nav-link-${l.id}`}>
              {l.label}
            </a>
          ))}
          <a href="#contacto" onClick={() => setMobileOpen(false)}
            className="btn-pill btn-pill-primary justify-center mt-4 w-full"
            style={{ display: "flex" }}>
            <CalendarHeart size={15} />
            Agenda tu sesión
          </a>
        </div>
      )}
    </header>
  );
};

export default Nav;
